import ManualOrderConsigneeDTO from "@/dto/manualOrder/ManualOrderConsigneeDTO";
import { addConsignee } from "@/services/api/manualOrder";
import { getFormLayout } from "@/utils";
import { useRequest } from "ahooks";
import { Button, Cascader, Form, Input, message } from "antd";
import { FormInstance } from "antd";
import React, { useCallback, useImperativeHandle } from "react";
import RecognizeAddressButton from "./RecognizeAddressButton";
import { useGlobalStore } from "@/store/hooks";

const [formItemLayout, formButtonLayout] = getFormLayout(8);

export interface ConsigneeFormValue {
    id?: number;

    name: string;

    mobile?: string;

    telephone?: string;

    area?: number[];

    street?: string;

    zipCode?: string;
}

interface AddOrUpdateConsigneeFormProps {
    shopId: number;

    onSuccess?(): void;

    onCancel?(): void;
}

const AddOrUpdateConsigneeForm = React.forwardRef<FormInstance<ConsigneeFormValue>, AddOrUpdateConsigneeFormProps>(
    (props, ref) => {
        const { shopId, onSuccess, onCancel } = props;
        const [form] = Form.useForm<ConsigneeFormValue>();
        const globalStore = useGlobalStore();

        // 对外转发
        useImperativeHandle(ref, () => form);

        const { runAsync: runAddOrUpdateConsignee, loading } = useRequest(addConsignee, {
            manual: true,
        });

        /**一键识别收货人回调 */
        const onRecognizeAddress = useCallback((consignee: ManualOrderConsigneeDTO) => {
            const area = [consignee.provinceCode, consignee.cityCode, consignee.districtCode];

            form.setFieldsValue({
                name: consignee.name,
                mobile: consignee.mobile,
                telephone: consignee.telephone,
                street: consignee.street,
                zipCode: consignee.zipCode,
                area,
            });
        }, []);

        // 保存收货人信息
        const handleSaveConsignee = useCallback(() => {
            (async () => {
                const values = await form.getFieldsValue();

                const { area } = values;

                const consignee: Partial<ManualOrderConsigneeDTO> = {
                    ...values,
                };

                if (area) {
                    const [provinceCode, cityCode, districtCode] = area;

                    consignee.provinceCode = provinceCode;
                    consignee.cityCode = cityCode;
                    consignee.districtCode = districtCode;
                }

                await runAddOrUpdateConsignee({ shopId, consignees: [consignee] as [ManualOrderConsigneeDTO] });

                message.success("保存成功");

                form.resetFields();

                onSuccess?.();
            })();
        }, [shopId]);

        return (
            <Form {...formItemLayout} form={form}>
                <Form.Item name="id" hidden noStyle>
                    <Input />
                </Form.Item>

                <Form.Item
                    label="姓名"
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: "收货人姓名不能为空",
                        },
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item label="手机" name="mobile">
                    <Input />
                </Form.Item>

                <Form.Item label="固话" name="telephone">
                    <Input />
                </Form.Item>

                <Form.Item label="省市区" name="area">
                    <Cascader
                        options={globalStore.areaStore.fhyAreas}
                        fieldNames={{
                            label: "name",
                            value: "id",
                            children: "children",
                        }}
                        placeholder="请选择省市区"
                        showSearch
                    />
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, next) => prev.street !== next.street}>
                    {(form) => {
                        const street = form.getFieldValue("street");

                        return (
                            <Form.Item
                                label="街道"
                                name="street"
                                extra={
                                    <RecognizeAddressButton
                                        value={street}
                                        shopId={shopId}
                                        onChange={onRecognizeAddress}
                                    />
                                }
                            >
                                <Input.TextArea
                                    rows={3}
                                    placeholder="可将收货人信息复制到这里然后点击“一键识别收货人”"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>

                <Form.Item label="邮编" name="zipCode">
                    <Input />
                </Form.Item>

                <Form.Item {...formButtonLayout}>
                    <Button type="primary" className="mr10" onClick={handleSaveConsignee} loading={loading}>
                        保存
                    </Button>

                    <Button onClick={onCancel} loading={loading}>
                        取消
                    </Button>
                </Form.Item>
            </Form>
        );
    },
);

export default AddOrUpdateConsigneeForm;
